import React from 'react'
import { Button } from '@/components/ui/button'
import { StarRating } from '@/components/ui/StarRating'
import { Edit2Icon, Edit3, Info, Shield } from 'lucide-react'
import type {
	PublicProgramInfo,
	ReviewStatistics,
} from '@/types/PublicReviewPage.types'
import { useTranslation } from '@/hooks/useTranslation'

interface SidebarProps {
	program: PublicProgramInfo
	statistics: ReviewStatistics | null
	handleWriteReview: () => void
	isPlaceholderData?: boolean
	isLoading?: boolean
}

export const PublicReviewPageSidebar: React.FC<SidebarProps> = ({
	program,
	statistics,
	handleWriteReview,
	isPlaceholderData,
	isLoading,
}) => {
	const { t } = useTranslation()
	return (
		<aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-6">
			{program.show_statistics &&
				statistics &&
				statistics.total_reviews > 0 && (
					<div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
						<h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
							{t('publicReviewPageSidebar.overallRating')}
						</h2>
						<div className="flex items-center mb-5">
							<div className="mr-4">
								<span className="text-5xl font-bold text-amber-500">
									{statistics.average_rating.toFixed(1)}
								</span>
							</div>
							<div className="flex flex-col">
								<StarRating
									rating={statistics.average_rating}
									size="large"
									className="mb-1.5"
								/>
								<p className="text-gray-500 dark:text-gray-400 text-sm">
									{t('publicReviewPageSidebar.basedOnVerifiedReviews', { count: statistics.total_reviews })}
								</p>
							</div>
						</div>

						<div className="space-y-3">
							{statistics.rating_distribution
								.sort((a, b) => b.rating - a.rating)
								.map((item) => (
									<div key={item.rating} className="flex items-center text-sm">
										<div className="w-16 text-gray-600 dark:text-gray-300 font-medium">
											{t('publicReviewPageSidebar.stars', { count: item.rating })}
										</div>
										<div className="flex-1 mx-2">
											<div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
												<div
													className="h-full bg-amber-400 rounded-full transition-all duration-500 ease-out"
													style={{
														width: `${
															statistics.total_reviews > 0
																? (item.count / statistics.total_reviews) * 100
																: 0
														}%`,
													}}
												/>
											</div>
										</div>
										<div className="w-6 text-right text-gray-500 dark:text-gray-400">
											{item.count}
										</div>
									</div>
								))}
						</div>
					</div>
				)}

			{program.allow_new_submissions && (
				<Button
					className="w-full py-2.5 text-base rounded-lg"
					onClick={handleWriteReview}
					disabled={isPlaceholderData || isLoading}>
					<Edit3 className="mr-2 h-4 w-4" /> {t('publicReviewPageSidebar.writeReview')}
				</Button>
			)}

			<div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
				<h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
					<Info size={18} className="mr-2  text-primary" />
					{t('publicReviewPageSidebar.aboutReviews')}
				</h2>
				<p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
					{t('publicReviewPageSidebar.reviewsDescription', { programName: program.name })}
				</p>
				<div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-4">
					<div className="flex items-start">
						<Shield
							className="text-indigo-600 dark:text-indigo-400 mr-3 mt-0.5 flex-shrink-0"
							size={20}
						/>
						<div>
							<h3 className="font-medium text-indigo-900 dark:text-indigo-200 mb-1">
								{t('publicReviewPageSidebar.verificationProcess')}
							</h3>
							<p className="text-indigo-700 dark:text-indigo-300 text-sm">
								{t('publicReviewPageSidebar.verificationDescription')}
							</p>
						</div>
					</div>
				</div>
			</div>
		</aside>
	)
}
